import {
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Redo,
  StrikethroughS,
  Undo,
} from "@mui/icons-material";
import { Divider, IconButton, SxProps, Theme, styled } from "@mui/material";
import { FORMAT_ELEMENT_COMMAND, FORMAT_TEXT_COMMAND, LexicalEditor, REDO_COMMAND, UNDO_COMMAND } from "lexical";
import { ReactNode } from "react";

const baseButtonStyles: SxProps<Theme> = {
  border: 0,
  display: "flex",
  background: "none",
  borderRadius: "10px",
  padding: "8px",
  cursor: "pointer",
  verticalAlign: "middle",
  "&:hover": {
    backgroundColor: "#eee",
  },
};

const activeButtonStyles: SxProps<Theme> = {
  ...baseButtonStyles,
  background: "rgba(223, 232, 250, 0.3)",
};

const spacedButtonStyles: SxProps<Theme> = {
  ...baseButtonStyles,
  marginRight: "2px",
};

const activeSpacedButtonStyles: SxProps<Theme> = {
  ...activeButtonStyles,
  marginRight: "2px",
};

const disabledButtonStyles: SxProps<Theme> = {
  ...baseButtonStyles,
  cursor: "not-allowed",
  opacity: 0.2,
  "&:hover": {},
};

const dividerStyles: SxProps<Theme> = {
  width: "1px",
  backgroundColor: "#eee",
  margin: "0 4px",
};

// Styled components for text formatting
export const StyledBoldText = styled("span")({
  fontWeight: "bold",
});

export const StyledItalicText = styled("span")({
  fontStyle: "italic",
});

export const StyledUnderlineText = styled("span")({
  textDecoration: "underline",
});

export const StyledStrikethroughText = styled("span")({
  textDecoration: "line-through",
});

export const StyledUnderlineStrikethroughText = styled("span")({
  textDecoration: "underline line-through",
});

export const StyledCodeText = styled("span")({
  backgroundColor: "#f4f4f4",
  border: "1px solid #e0e0e0",
  borderRadius: "3px",
  padding: "1px 3px",
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: "0.9em",
});

export const StyledHashtagText = styled("span")({
  color: "#0052cc",
  fontWeight: 500,
});

export const StyledOverflowedText = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const getStyledTextComponent = (format: string) => {
  switch (format) {
    case "bold":
      return StyledBoldText;
    case "italic":
      return StyledItalicText;
    case "underline":
      return StyledUnderlineText;
    case "strikethrough":
      return StyledStrikethroughText;
    case "underlineStrikethrough":
      return StyledUnderlineStrikethroughText;
    case "code":
      return StyledCodeText;
    case "hashtag":
      return StyledHashtagText;
    case "overflowed":
      return StyledOverflowedText;
    default:
      return "span";
  }
};

// Action Button Components
interface ActionButtonProps {
  editor: LexicalEditor;
  command: typeof UNDO_COMMAND | typeof REDO_COMMAND;
  disabled: boolean;
  ariaLabel: string;
  icon: ReactNode;
}

export function ActionButton({ editor, command, disabled, ariaLabel, icon }: ActionButtonProps) {
  return (
    <IconButton
      disabled={disabled}
      onClick={() => {
        editor.dispatchCommand(command, undefined);
      }}
      aria-label={ariaLabel}
      sx={disabled ? disabledButtonStyles : baseButtonStyles}
    >
      {icon}
    </IconButton>
  );
}

// Format Button Components
interface FormatButtonProps {
  editor: LexicalEditor;
  format: "bold" | "italic" | "underline" | "strikethrough";
  isActive: boolean;
  ariaLabel: string;
  icon: ReactNode;
}

export function FormatButton({ editor, format, isActive, ariaLabel, icon }: FormatButtonProps) {
  return (
    <IconButton
      onClick={() => {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
      }}
      aria-label={ariaLabel}
      sx={isActive ? activeSpacedButtonStyles : spacedButtonStyles}
    >
      {icon}
    </IconButton>
  );
}

interface AlignmentButtonProps {
  editor: LexicalEditor;
  alignment: "left" | "center" | "right" | "justify";
  isActive: boolean;
  onAlignmentChange: (alignment: string) => void;
  ariaLabel: string;
  icon: ReactNode;
}

export function AlignmentButton({
  editor,
  alignment,
  isActive,
  onAlignmentChange,
  ariaLabel,
  icon,
}: AlignmentButtonProps) {
  return (
    <IconButton
      onClick={() => {
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);
        onAlignmentChange(alignment);
      }}
      aria-label={ariaLabel}
      sx={isActive ? activeSpacedButtonStyles : spacedButtonStyles}
    >
      {icon}
    </IconButton>
  );
}

// Divider Component
export function ToolbarDivider() {
  return <Divider sx={dividerStyles} />;
}

// Specific Button Components
export function UndoButton({ editor, disabled }: { editor: LexicalEditor; disabled: boolean }) {
  return <ActionButton editor={editor} command={UNDO_COMMAND} disabled={disabled} ariaLabel="Undo" icon={<Undo />} />;
}

export function RedoButton({ editor, disabled }: { editor: LexicalEditor; disabled: boolean }) {
  return <ActionButton editor={editor} command={REDO_COMMAND} disabled={disabled} ariaLabel="Redo" icon={<Redo />} />;
}

export function BoldButton({ editor, isActive }: { editor: LexicalEditor; isActive: boolean }) {
  return (
    <FormatButton editor={editor} format="bold" isActive={isActive} ariaLabel="Format Bold" icon={<FormatBold />} />
  );
}

export function ItalicButton({ editor, isActive }: { editor: LexicalEditor; isActive: boolean }) {
  return (
    <FormatButton
      editor={editor}
      format="italic"
      isActive={isActive}
      ariaLabel="Format Italics"
      icon={<FormatItalic />}
    />
  );
}

export function UnderlineButton({ editor, isActive }: { editor: LexicalEditor; isActive: boolean }) {
  return (
    <FormatButton
      editor={editor}
      format="underline"
      isActive={isActive}
      ariaLabel="Format Underline"
      icon={<FormatUnderlined />}
    />
  );
}

export function StrikethroughButton({ editor, isActive }: { editor: LexicalEditor; isActive: boolean }) {
  return (
    <FormatButton
      editor={editor}
      format="strikethrough"
      isActive={isActive}
      ariaLabel="Format Strikethrough"
      icon={<StrikethroughS />}
    />
  );
}

export function LeftAlignButton({
  editor,
  isActive,
  onAlignmentChange,
}: {
  editor: LexicalEditor;
  isActive: boolean;
  onAlignmentChange: (alignment: string) => void;
}) {
  return (
    <AlignmentButton
      editor={editor}
      alignment="left"
      isActive={isActive}
      onAlignmentChange={onAlignmentChange}
      ariaLabel="Left Align"
      icon={<FormatAlignLeft />}
    />
  );
}

export function CenterAlignButton({
  editor,
  isActive,
  onAlignmentChange,
}: {
  editor: LexicalEditor;
  isActive: boolean;
  onAlignmentChange: (alignment: string) => void;
}) {
  return (
    <AlignmentButton
      editor={editor}
      alignment="center"
      isActive={isActive}
      onAlignmentChange={onAlignmentChange}
      ariaLabel="Center Align"
      icon={<FormatAlignCenter />}
    />
  );
}

export function RightAlignButton({
  editor,
  isActive,
  onAlignmentChange,
}: {
  editor: LexicalEditor;
  isActive: boolean;
  onAlignmentChange: (alignment: string) => void;
}) {
  return (
    <AlignmentButton
      editor={editor}
      alignment="right"
      isActive={isActive}
      onAlignmentChange={onAlignmentChange}
      ariaLabel="Right Align"
      icon={<FormatAlignRight />}
    />
  );
}

export function JustifyAlignButton({
  editor,
  isActive,
  onAlignmentChange,
}: {
  editor: LexicalEditor;
  isActive: boolean;
  onAlignmentChange: (alignment: string) => void;
}) {
  return (
    <AlignmentButton
      editor={editor}
      alignment="justify"
      isActive={isActive}
      onAlignmentChange={onAlignmentChange}
      ariaLabel="Justify Align"
      icon={<FormatAlignJustify />}
    />
  );
}
